import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Lock, LockOpen, KeyRound, Search, ShieldAlert, ShieldCheck, Trash2, IdCard } from "lucide-react";
import { ROLES, roleLabel } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ADMIN_ROLES = ["super_admin", "founder", "hr_admin"];

export default function AdminUsers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [pwdReset, setPwdReset] = useState(null); // {id,email}
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/admin", { params: { q, include_inactive: showInactive } });
      setRows(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  }, [q, showInactive]);
  useEffect(() => { load(); }, [load]);

  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  if (!isAdmin) {
    return (
      <AppLayout title="Users">
        <div data-testid="admin-users-denied" className="max-w-md mx-auto mt-16 miro-card p-8 text-center">
          <ShieldAlert className="mx-auto mb-3 text-red-500" size={30}/>
          <div className="font-display font-black text-xl text-[var(--miro-navy)]">Access denied</div>
          <div className="text-sm text-[var(--miro-text-secondary)] mt-1">
            Only Super Admin, Founder or HR Admin can manage users.
          </div>
        </div>
      </AppLayout>
    );
  }

  const setActive = async (u, active) => {
    try {
      await api.patch(`/users/admin/${u.id}`, { active });
      toast.success(active ? "User re-activated" : "User deactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/admin/${u.id}`, { role });
      toast.success(`Role updated to ${roleLabel(role)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const lock = async (u) => {
    if (!window.confirm(`Lock ${u.email} for 24 hours?`)) return;
    try {
      await api.post(`/users/admin/${u.id}/lock`, null, { params: { minutes: 1440 } });
      toast.success("Account locked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lock failed"); }
  };
  const unlock = async (u) => {
    try {
      await api.post(`/users/admin/${u.id}/unlock`);
      toast.success("Account unlocked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Unlock failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(
      `Permanently DELETE ${u.email}?\n\n` +
      `This removes the user account and their sessions.\n` +
      `CRM records they own are NOT deleted (use "Purge demo data" for that).\n\n` +
      `This cannot be undone.`
    )) return;
    if (!window.confirm(`Final confirmation — delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/admin/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <AppLayout title="Users & Access">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-[var(--miro-border)] rounded-lg px-3 py-2 flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="text-[var(--miro-text-muted)]"/>
          <input data-testid="admin-users-search" className="bg-transparent outline-none text-sm flex-1"
                 placeholder="Search users by name or email…" value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--miro-text-secondary)]">
          <input type="checkbox" data-testid="admin-users-show-inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}/>
          Show inactive
        </label>
        <div className="ml-auto flex gap-2">
          <button
            data-testid="admin-users-force-reset-all"
            className="px-3 py-2 rounded-lg border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-50 transition-colors"
            onClick={async () => {
              if (!window.confirm(
                "Force ALL non-super-admin users to change their password on next login? "
                + "Their sessions will be invalidated immediately. This action is audited."
              )) return;
              try {
                const r = await api.post("/users/admin/force-password-reset-all");
                alert(`OK — ${r.data.affected} users must reset on next login.`);
                load();
              } catch (e) {
                alert(e?.response?.data?.detail || "Bulk reset failed");
              }
            }}
          >
            Force password reset (all)
          </button>
          <button
            data-testid="admin-users-purge-demo"
            className="px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-red-800 text-xs font-semibold hover:bg-red-100 transition-colors"
            onClick={async () => {
              const wipeAll = window.confirm(
                "PURGE DEMO DATA — this permanently deletes:\n\n"
                + "• Known demo user accounts (admin@miro.one, counsellor@miro.one, "
                + "wealth@miro.one, partner@miro.one, student@miro.one, external@miro.one, "
                + "mastermiro@miroconsultancy.co.in)\n\n"
                + "Click OK for FULL PRODUCTION RESET:\n"
                + "  • Deletes EVERY non-admin user (except you and real super_admins)\n"
                + "  • Deletes ALL leads, contacts, opportunities, tasks, quotations, "
                + "invoices, partner applications, payments, agreements, digital IDs, "
                + "enrolments, applications, programs, communications\n"
                + "  • Use this ONLY when production has no real business data yet.\n\n"
                + "Click Cancel to purge ONLY the known demo emails and their "
                + "directly-attributed CRM rows (leaves other data intact)."
              );
              if (!window.confirm(
                "Final confirmation — this cannot be undone. Proceed?"
              )) return;
              try {
                const r = await api.post(
                  `/users/admin/purge-demo-data?confirm=PURGE-DEMO-DATA`
                  + (wipeAll ? `&include_all_non_admin=true` : ``)
                );
                const d = r.data;
                const crmLines = Object.entries(d.crm_deleted || {})
                  .map(([k, v]) => `${k}: ${v}`).join("\n");
                alert(
                  `Purged.\n\n`
                  + `Users deleted: ${d.users_deleted}\n`
                  + `Sessions dropped: ${d.sessions_deleted}\n\n`
                  + `CRM rows:\n${crmLines || "(none)"}`
                );
                load();
              } catch (e) {
                alert(e?.response?.data?.detail || "Purge failed");
              }
            }}
          >
            Purge demo data
          </button>
          <NewUserDialog open={createOpen} setOpen={setCreateOpen} onCreated={load}/>
        </div>
      </div>

      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
        <table data-testid="admin-users-table" className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Last login</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">
                {loading ? "Loading users…" : "No users found."}
              </td></tr>
            )}
            {rows.map(u => {
              const isSelf = u.id === user?.id;
              return (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-[var(--miro-border)]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--miro-navy)] text-white flex items-center justify-center font-display font-bold">
                      {u.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--miro-navy)]">
                        {u.name} {isSelf && <span className="text-[10px] uppercase tracking-wider text-[var(--miro-blue)] ml-1">you</span>}
                      </div>
                      <div className="text-xs text-[var(--miro-text-muted)]">{u.email}</div>
                      {u.employee_code && (
                        <div className="text-[10px] font-mono text-[var(--miro-blue)] mt-0.5"
                             data-testid={`admin-user-emp-code-${u.id}`}>
                          {u.employee_code}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={isSelf}>
                    <SelectTrigger data-testid={`admin-user-role-${u.id}`} className="h-8 w-48 text-xs">
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    {u.locked ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700 w-fit">
                        <Lock size={11}/> Locked
                      </span>
                    ) : u.active ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-green-100 text-green-700 w-fit">
                        <ShieldCheck size={11}/> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-slate-200 text-slate-700 w-fit">
                        Inactive
                      </span>
                    )}
                    {u.password_must_change && (
                      <span className="text-[10px] text-orange-600">Password reset pending</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-[var(--miro-text-secondary)] text-xs">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                  {u.last_login_ip && <div className="text-[10px] text-[var(--miro-text-muted)]">{u.last_login_ip}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {u.locked ? (
                      <Button data-testid={`admin-user-unlock-${u.id}`} size="sm" variant="outline"
                              onClick={() => unlock(u)} title="Unlock">
                        <LockOpen size={13} className="mr-1"/> Unlock
                      </Button>
                    ) : (
                      <Button data-testid={`admin-user-lock-${u.id}`} size="sm" variant="outline"
                              disabled={isSelf} onClick={() => lock(u)} title="Lock for 24h">
                        <Lock size={13} className="mr-1"/> Lock
                      </Button>
                    )}
                    <Button data-testid={`admin-user-reset-${u.id}`} size="sm" variant="outline"
                            onClick={() => setPwdReset(u)} title="Reset password">
                      <KeyRound size={13} className="mr-1"/> Reset
                    </Button>
                    {u.employee_code && (
                      <Button
                        data-testid={`admin-user-id-card-${u.id}`}
                        size="sm" variant="outline"
                        title="Download printable ID card (PDF)"
                        onClick={async () => {
                          try {
                            const r = await api.get(
                              `/digital-ids/${u.employee_code}/card.pdf`,
                              { responseType: "blob" }
                            );
                            const url = window.URL.createObjectURL(
                              new Blob([r.data], { type: "application/pdf" })
                            );
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `MIRO_ID_${u.employee_code}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch (e) {
                            toast.error(
                              e?.response?.data?.detail
                                || "Could not fetch ID card"
                            );
                          }
                        }}
                      >
                        <IdCard size={13} className="mr-1"/> ID Card
                      </Button>
                    )}
                    <Button data-testid={`admin-user-active-${u.id}`} size="sm" variant="outline"
                            disabled={isSelf} onClick={() => setActive(u, !u.active)}>
                      {u.active ? "Deactivate" : "Activate"}
                    </Button>
                    <Button data-testid={`admin-user-remove-${u.id}`} size="sm" variant="outline"
                            disabled={isSelf || u.role === "super_admin" || u.role === "founder"}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => remove(u)} title="Delete user">
                      <Trash2 size={13} className="mr-1"/> Remove
                    </Button>
                  </div>
                </td>
              </tr>);
            })}
          </tbody>
        </table>
      </div>

      <ResetPasswordDialog target={pwdReset} onClose={() => setPwdReset(null)} onDone={load}/>
    </AppLayout>
  );
}

function NewUserDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", role: "employee", password: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/users/admin", f);
      toast.success(
        `User created. Invite email sent to ${f.email}. ` +
        `They must change password on first login.`
      );
      setOpen(false);
      setF({ name: "", email: "", role: "employee", password: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="admin-new-user-btn" style={{ background: "var(--miro-navy)" }}>
          <Plus size={16} className="mr-1"/> New user
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New employee account</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Full name *</Label><Input data-testid="admin-new-user-name" value={f.name} onChange={e=>upd("name",e.target.value)} className="mt-1"/></div>
          <div><Label>Work email *</Label><Input data-testid="admin-new-user-email" type="email" value={f.email} onChange={e=>upd("email",e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Role</Label>
            <Select value={f.role} onValueChange={v=>upd("role",v)}>
              <SelectTrigger data-testid="admin-new-user-role" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Temporary password *</Label>
            <Input data-testid="admin-new-user-password" type="text" value={f.password} onChange={e=>upd("password",e.target.value)} className="mt-1"
                   placeholder="Min 8 chars · mix of upper/lower/digit/symbol"/>
            <div className="text-[11px] text-[var(--miro-text-muted)] mt-1">The user will be forced to change this on first login.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="admin-new-user-submit"
                  disabled={!f.name.trim()||!f.email.trim()||!f.password.trim()||saving}
                  onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ target, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPwd(""); }, [target?.id]);
  if (!target) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/users/admin/${target.id}/reset-password`, { new_password: pwd });
      toast.success("Password reset. User must change it at next login.");
      onDone?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">Reset password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-[var(--miro-text-secondary)]">
            User: <span className="font-semibold text-[var(--miro-navy)]">{target.email}</span>
          </div>
          <div>
            <Label>New temporary password</Label>
            <Input data-testid="admin-reset-pwd-input" type="text" value={pwd} onChange={e=>setPwd(e.target.value)} className="mt-1"
                   placeholder="Min 8 chars · mix of upper/lower/digit/symbol"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="admin-reset-pwd-submit"
                  disabled={pwd.length < 8 || saving} onClick={submit}
                  style={{ background: "var(--miro-navy)" }}>
            {saving ? "Resetting…" : "Reset & force change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
